/**
 * Opt-in gift with purchase.
 * Config: window.GiftWithPurchaseConfig (gift-with-purchase-config.liquid)
 */
(function () {
  "use strict";

  var DEBOUNCE_MS = 800;
  var GIFT_PROPERTY = "_gift";
  var SUCCESS_MESSAGE_MS = 3000;

  function isInventorySoldOutError(data) {
    var msg = String(
      (data && (data.description || data.message)) || ""
    ).toLowerCase();
    return (
      msg.indexOf("sold out") !== -1 ||
      msg.indexOf("inventory") !== -1 ||
      msg.indexOf("not enough") !== -1 ||
      msg.indexOf("available stock") !== -1
    );
  }

  function GiftWithPurchase() {
    this.config = window.GiftWithPurchaseConfig || {};
    this.isUpdating = false;
    this.isCheckoutValidating = false;
    this.skipNextCheckoutGuard = false;
    this.debounceTimer = null;
    this.soldOut = false;
    this.lastBannerState = { eligible: false, hasGift: false };
    this.successMessageTimer = null;
    this.init();
  }

  Object.defineProperty(GiftWithPurchase.prototype, "enabled", {
    get: function () {
      return Boolean(this.config.enabled && this.config.giftVariantId);
    },
  });

  GiftWithPurchase.prototype.getEligibleIds = function () {
    return new Set(
      (this.config.eligibleProductIds || []).map(function (id) {
        return Number(id);
      })
    );
  };

  GiftWithPurchase.prototype.getGiftVariantId = function () {
    return Number(this.config.giftVariantId);
  };

  GiftWithPurchase.prototype.init = function () {
    var self = this;
    if (!this.enabled) return;

    document.addEventListener("cart:updated", function (event) {
      var cart = event.detail && event.detail.cart;
      if (
        cart &&
        self.findGiftLines(cart).some(function (line) {
          return line.quantity > 1;
        })
      ) {
        self.check(cart);
        return;
      }
      self.scheduleCheck(cart);
    });

    document.addEventListener("ajaxProduct:added", function () {
      self.scheduleCheck();
    });

    document.addEventListener("cart:build", function () {
      self.scheduleCheck();
    });

    document.addEventListener("click", function (event) {
      var button = event.target.closest("[data-gwp-add]");
      if (!button || button.disabled) return;
      event.preventDefault();
      self.addGift(button);
    });

    var replayBannerOnDrawerOpen = function () {
      if (self.lastBannerState.eligible && !self.lastBannerState.hasGift) {
        document.querySelectorAll("[data-gwp-offer]").forEach(function (element) {
          self.showBanner(element, true);
        });
      }
    };

    document.addEventListener("drawerOpen", replayBannerOnDrawerOpen);
    document.addEventListener("drawerOpen.CartDrawer", replayBannerOnDrawerOpen);

    this.bindCheckoutGuard("CartDrawerForm");
    this.bindCheckoutGuard("CartPageForm");

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", function () {
        self.scheduleCheck();
      });
    } else {
      this.scheduleCheck();
    }
  };

  GiftWithPurchase.prototype.bindCheckoutGuard = function (formId) {
    var self = this;
    var form = document.getElementById(formId);
    if (!form || form.dataset.gwpCheckoutGuard === "true") return;

    form.dataset.gwpCheckoutGuard = "true";
    form.addEventListener("submit", function (event) {
      if (self.skipNextCheckoutGuard) {
        self.skipNextCheckoutGuard = false;
        return;
      }

      if (!self.enabled || self.isCheckoutValidating) return;

      var submitter = event.submitter;
      if (submitter && submitter.name !== "checkout") return;

      event.preventDefault();
      self.isCheckoutValidating = true;

      self
        .validateCartForCheckout()
        .then(function () {
          self.isCheckoutValidating = false;
          self.skipNextCheckoutGuard = true;
          if (typeof form.requestSubmit === "function" && submitter) {
            form.requestSubmit(submitter);
          } else if (typeof form.requestSubmit === "function") {
            form.requestSubmit();
          } else {
            form.submit();
          }
        })
        .catch(function (error) {
          self.isCheckoutValidating = false;
          console.error("GiftWithPurchase checkout validation failed", error);
        });
    });
  };

  GiftWithPurchase.prototype.scheduleCheck = function (cart) {
    var self = this;
    if (!this.enabled) return;
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(function () {
      self.check(cart);
    }, DEBOUNCE_MS);
  };

  GiftWithPurchase.prototype.fetchCart = function () {
    return fetch("/cart.js", { credentials: "same-origin" }).then(function (
      response
    ) {
      return response.json();
    });
  };

  GiftWithPurchase.prototype.isGiftLine = function (item) {
    return (
      item.variant_id === this.getGiftVariantId() ||
      item.properties[GIFT_PROPERTY] === "true"
    );
  };

  GiftWithPurchase.prototype.isEligible = function (cart) {
    var self = this;
    var eligibleIds = this.getEligibleIds();
    return cart.items.some(function (item) {
      return (
        eligibleIds.has(item.product_id) && !self.isGiftLine(item)
      );
    });
  };

  GiftWithPurchase.prototype.findGiftLines = function (cart) {
    var self = this;
    return cart.items.filter(function (item) {
      return self.isGiftLine(item);
    });
  };

  GiftWithPurchase.prototype.refreshCartMarkup = function () {
    document.dispatchEvent(new CustomEvent("cart:build"));
  };

  GiftWithPurchase.prototype.reconcileCart = function (cart) {
    var self = this;
    var eligible = this.isEligible(cart);
    var giftLines = this.findGiftLines(cart);
    var hasGift = giftLines.length > 0;

    if (!eligible && hasGift) {
      return this.removeGifts(giftLines).then(function () {
        return self.fetchCart();
      });
    }

    if (hasGift && giftLines.some(function (line) { return line.quantity > 1; })) {
      return this.setGiftQuantity(giftLines[0], 1).then(function () {
        return self.fetchCart();
      });
    }

    this.updateBanners({
      eligible: eligible,
      hasGift: hasGift,
      soldOut: this.soldOut,
    });

    return Promise.resolve(cart);
  };

  GiftWithPurchase.prototype.check = function (existingCart) {
    var self = this;
    if (!this.enabled || this.isUpdating) return Promise.resolve();

    var cartPromise = existingCart && existingCart.items
      ? Promise.resolve(existingCart)
      : this.fetchCart();

    return cartPromise
      .then(function (cart) {
        return self.reconcileCart(cart);
      })
      .catch(function (error) {
        console.error("GiftWithPurchase check failed", error);
      });
  };

  GiftWithPurchase.prototype.validateCartForCheckout = function () {
    var self = this;
    if (!this.enabled) return Promise.resolve();

    return this.fetchCart().then(function (cart) {
      return self.reconcileCart(cart);
    });
  };

  GiftWithPurchase.prototype.announceGiftAdded = function (message) {
    if (!message) return;

    var liveRegion = document.getElementById("cart-announcements");
    if (!liveRegion) {
      liveRegion = document.createElement("div");
      liveRegion.id = "cart-announcements";
      liveRegion.setAttribute("aria-live", "polite");
      liveRegion.setAttribute("aria-atomic", "true");
      liveRegion.className = "sr-only";
      document.body.appendChild(liveRegion);
    }

    liveRegion.textContent = message;
    window.setTimeout(function () {
      if (liveRegion.textContent === message) {
        liveRegion.textContent = "";
      }
    }, 1000);
  };

  GiftWithPurchase.prototype.showSuccessMessage = function (message) {
    var self = this;
    if (!message) return;

    this.announceGiftAdded(message);

    var successNodes = document.querySelectorAll("[data-gwp-success]");
    if (!successNodes.length) return;

    window.clearTimeout(this.successMessageTimer);
    successNodes.forEach(function (node) {
      node.textContent = message;
      node.hidden = false;
      node.classList.add("cart__gwp-offer-success--visible");
    });

    this.successMessageTimer = window.setTimeout(function () {
      successNodes.forEach(function (node) {
        node.hidden = true;
        node.classList.remove("cart__gwp-offer-success--visible");
        node.textContent = "";
      });
    }, SUCCESS_MESSAGE_MS);
  };

  GiftWithPurchase.prototype.showBanner = function (element, forceAnimate) {
    if (!element) return;

    var isVisible = !element.classList.contains("cart__gwp-offer--collapsed");
    if (isVisible && !forceAnimate) return;

    element.classList.remove("cart__gwp-offer--collapsed");
    element.setAttribute("aria-hidden", "false");

    if (
      forceAnimate &&
      element.classList.contains("appear-animation")
    ) {
      element.classList.remove("appear-animation");
      void element.offsetHeight;
      element.classList.add("appear-animation");
    }
  };

  GiftWithPurchase.prototype.hideBanner = function (element) {
    if (!element || element.classList.contains("cart__gwp-offer--collapsed")) return;

    element.classList.add("cart__gwp-offer--collapsed");
    element.setAttribute("aria-hidden", "true");
  };

  GiftWithPurchase.prototype.updateBanners = function (state) {
    var self = this;
    this.lastBannerState = state;

    document.querySelectorAll("[data-gwp-offer]").forEach(function (element) {
      var headline = element.querySelector("[data-gwp-headline]");
      var cta = element.querySelector("[data-gwp-add]");
      var status = element.querySelector("[data-gwp-status]");

      if (!state.eligible || state.hasGift) {
        self.hideBanner(element);
        if (status) status.textContent = "";
        return;
      }

      self.showBanner(element);
      var productTitle = element.querySelector("[data-gwp-product-title]");
      if (productTitle && self.config.giftProductTitle) {
        productTitle.textContent = self.config.giftProductTitle;
      }
      if (headline) {
        headline.textContent = self.config.headline || "";
      }

      if (state.soldOut) {
        if (cta) {
          cta.disabled = true;
          cta.hidden = true;
        }
        if (status) {
          status.textContent = self.config.soldOutMessage || "";
        }
      } else {
        if (cta) {
          cta.disabled = false;
          cta.hidden = false;
          cta.textContent = self.config.ctaLabel || "Add free speaker";
        }
        if (status) status.textContent = "";
      }
    });
  };

  GiftWithPurchase.prototype.addGift = function (button) {
    var self = this;
    if (this.isUpdating) return Promise.resolve();

    this.soldOut = false;
    this.isUpdating = true;
    var originalText = button.textContent;
    button.disabled = true;
    button.textContent = this.config.addingLabel || "Adding…";

    var offerElement = button.closest("[data-gwp-offer]");
    var statusElement = offerElement && offerElement.querySelector("[data-gwp-status]");
    if (statusElement) statusElement.textContent = "";

    var addUrl =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + "cart/add.js"
        : "/cart/add.js";

    return this.fetchCart()
      .then(function (cart) {
        if (self.findGiftLines(cart).length > 0) {
          self.updateBanners({
            eligible: self.isEligible(cart),
            hasGift: true,
            soldOut: self.soldOut,
          });
          throw new Error("Gift already in cart");
        }

        return fetch(addUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({
            items: [
              {
                id: self.getGiftVariantId(),
                quantity: 1,
                properties: { _gift: "true" },
              },
            ],
          }),
        });
      })
      .then(function (response) {
        if (!response) return null;
        return response.json().then(function (data) {
          return { ok: response.ok, status: response.status, data: data };
        });
      })
      .then(function (result) {
        if (!result) return null;

        if (!result.ok) {
          var errorMsg =
            result.data.description ||
            result.data.message ||
            "Could not add free gift";

          if (result.status === 422 && isInventorySoldOutError(result.data)) {
            self.soldOut = true;
            self.updateBanners({
              eligible: true,
              hasGift: false,
              soldOut: true,
            });
          } else {
            self.soldOut = false;
            self.updateBanners({
              eligible: true,
              hasGift: false,
              soldOut: false,
            });
            if (statusElement) {
              statusElement.textContent = errorMsg;
            }
          }

          throw new Error(errorMsg);
        }

        self.soldOut = false;
        self.showSuccessMessage(
          self.config.addedMessage || "Free speaker added to your order"
        );

        self.refreshCartMarkup();
        document.dispatchEvent(
          new CustomEvent("ajaxProduct:added", { detail: result.data })
        );

        document.querySelectorAll("[data-gwp-offer]").forEach(function (el) {
          self.hideBanner(el);
        });

        return self.fetchCart().then(function (cart) {
          var giftLines = self.findGiftLines(cart);
          var finalize = Promise.resolve(cart);

          if (giftLines.some(function (line) { return line.quantity > 1; })) {
            finalize = self.setGiftQuantity(giftLines[0], 1).then(function () {
              return self.fetchCart();
            });
          }

          return finalize.then(function (finalCart) {
            document.dispatchEvent(
              new CustomEvent("cart:updated", { detail: { cart: finalCart } })
            );
            return finalCart;
          });
        });
      })
      .catch(function (error) {
        if (error && error.message === "Gift already in cart") {
          button.disabled = false;
          button.textContent = originalText;
          return;
        }
        console.error("GiftWithPurchase add failed", error);
        button.disabled = false;
        button.textContent = originalText;
      })
      .finally(function () {
        self.isUpdating = false;
      });
  };

  GiftWithPurchase.prototype.removeGifts = function (giftLines) {
    var self = this;
    this.isUpdating = true;

    var chain = Promise.resolve();
    giftLines.forEach(function (line) {
      chain = chain.then(function () {
        return fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        });
      });
    });

    return chain
      .then(function () {
        return self.fetchCart();
      })
      .then(function (cart) {
        self.refreshCartMarkup();
        document.dispatchEvent(
          new CustomEvent("cart:updated", { detail: { cart: cart } })
        );
        self.updateBanners({
          eligible: self.isEligible(cart),
          hasGift: false,
          soldOut: self.soldOut,
        });
        return cart;
      })
      .catch(function (error) {
        console.error("GiftWithPurchase remove failed", error);
      })
      .finally(function () {
        self.isUpdating = false;
      });
  };

  GiftWithPurchase.prototype.setGiftQuantity = function (line, quantity) {
    var self = this;
    this.isUpdating = true;

    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: line.key, quantity: quantity }),
    })
      .then(function () {
        return self.fetchCart();
      })
      .then(function (cart) {
        self.refreshCartMarkup();
        document.dispatchEvent(
          new CustomEvent("cart:updated", { detail: { cart: cart } })
        );
        self.updateBanners({
          eligible: self.isEligible(cart),
          hasGift: self.findGiftLines(cart).length > 0,
          soldOut: self.soldOut,
        });
        return cart;
      })
      .catch(function (error) {
        console.error("GiftWithPurchase quantity fix failed", error);
      })
      .finally(function () {
        self.isUpdating = false;
      });
  };

  window.GiftWithPurchase = new GiftWithPurchase();
})();
